/**
 * Drawer Component
 * Reusable side drawer with AJAX content loading
 */

import { api } from '../utils/api.js';

/**
 * Default loading spinner HTML
 */
const LOADING_SPINNER = `
  <div class="flex items-center justify-center py-12">
    <svg class="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  </div>
`;

/**
 * Drawer class for managing side drawers
 */
export class Drawer {
  /**
   * Create a Drawer instance
   * @param {object} options - Configuration options
   * @param {string} options.drawerId - ID of the drawer element
   * @param {string} options.backdropId - ID of the backdrop element (optional)
   * @param {string} options.contentSelector - Selector for content container
   * @param {string} options.closeSelector - Selector for close buttons
   * @param {string} options.position - Drawer position: 'right' (default), 'left', 'top', 'bottom'
   * @param {Function} options.onOpen - Callback after drawer opens
   * @param {Function} options.onClose - Callback after drawer closes
   * @param {Function} options.onContentLoaded - Callback after content is loaded
   */
  constructor(options) {
    this.drawerId = options.drawerId;
    this.backdropId = options.backdropId;
    this.contentSelector = options.contentSelector || '.drawer-content';
    this.closeSelector = options.closeSelector || '[data-drawer-close]';
    this.position = options.position || 'right';
    this.onOpen = options.onOpen;
    this.onClose = options.onClose;
    this.onContentLoaded = options.onContentLoaded;

    // State
    this.currentUrl = null;
    this.currentData = null;
    this.isLoading = false;

    // Elements
    this.drawer = document.getElementById(this.drawerId);
    this.backdrop = this.backdropId ? document.getElementById(this.backdropId) : null;
    this.content = this.drawer?.querySelector(this.contentSelector);

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // Initialize
    this.bindEvents();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Close on backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.close);
    }

    // Close buttons
    this.drawer?.querySelectorAll(this.closeSelector).forEach((btn) => {
      btn.addEventListener('click', this.close);
    });

    // Close on Escape key
    document.addEventListener('keydown', this.handleKeyDown);
  }

  /**
   * Handle keydown events
   */
  handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }

  /**
   * Check if drawer is open
   */
  isOpen() {
    return this.drawer && !this.drawer.classList.contains('translate-x-full');
  }

  /**
   * Get animation classes based on position
   */
  getAnimationClasses() {
    const classes = {
      right: { hidden: 'translate-x-full', visible: 'translate-x-0' },
      left: { hidden: '-translate-x-full', visible: 'translate-x-0' },
      top: { hidden: '-translate-y-full', visible: 'translate-y-0' },
      bottom: { hidden: 'translate-y-full', visible: 'translate-y-0' },
    };
    return classes[this.position] || classes.right;
  }

  /**
   * Open the drawer
   * @param {string} url - URL to load content from (optional)
   * @param {object} data - Additional data to pass (optional)
   */
  async open(url = null, data = null) {
    if (!this.drawer) return;

    this.currentUrl = url;
    this.currentData = data;

    const animClasses = this.getAnimationClasses();

    // Show loading if URL provided
    if (url && this.content) {
      this.content.innerHTML = LOADING_SPINNER;
    }

    // Show drawer with animation
    this.drawer.classList.remove(animClasses.hidden);
    this.drawer.classList.add(animClasses.visible);

    // Show backdrop
    if (this.backdrop) {
      this.backdrop.classList.remove('hidden', 'opacity-0');
      this.backdrop.classList.add('opacity-100');
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Load content if URL provided
    if (url) {
      await this.loadContent();
    }

    // Callback
    if (this.onOpen) {
      this.onOpen(this);
    }
  }

  /**
   * Close the drawer
   */
  close() {
    if (!this.drawer) return;

    const animClasses = this.getAnimationClasses();

    // Animate out
    this.drawer.classList.remove(animClasses.visible);
    this.drawer.classList.add(animClasses.hidden);

    // Hide backdrop
    if (this.backdrop) {
      this.backdrop.classList.remove('opacity-100');
      this.backdrop.classList.add('opacity-0');
      setTimeout(() => {
        this.backdrop.classList.add('hidden');
      }, 200);
    }

    // Restore body scroll
    document.body.style.overflow = '';

    // Reset state
    this.currentUrl = null;
    this.currentData = null;

    // Callback
    if (this.onClose) {
      this.onClose(this);
    }
  }

  /**
   * Load content from URL
   */
  async loadContent() {
    if (!this.content || !this.currentUrl) return;

    this.isLoading = true;

    try {
      const html = await api(this.currentUrl, { method: 'GET' }, { showErrorToast: false });
      this.content.innerHTML = html;

      // Re-execute scripts
      this.executeScripts();

      if (typeof window.reinitFlowbiteWidgets === 'function') {
        window.reinitFlowbiteWidgets(this.content || undefined);
      }

      // Attach close buttons in loaded content
      this.content.querySelectorAll(this.closeSelector).forEach((btn) => {
        btn.addEventListener('click', this.close);
      });

      // Callback
      if (this.onContentLoaded) {
        this.onContentLoaded(this);
      }
    } catch (error) {
      console.error('Error loading drawer content:', error);
      this.content.innerHTML = `
        <div class="text-center py-12">
          <p class="text-red-600 dark:text-red-400">Error loading content. Please try again.</p>
          <button type="button" class="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300" onclick="this.closest('${this.contentSelector}').dispatchEvent(new CustomEvent('retry'))">
            Retry
          </button>
        </div>
      `;
      this.content.addEventListener(
        'retry',
        () => {
          this.loadContent();
        },
        { once: true }
      );
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Execute scripts in loaded content
   */
  executeScripts() {
    if (!this.content) return;

    this.content.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /**
   * Update drawer content without loading from URL
   * @param {string} html - HTML content to set
   */
  setContent(html) {
    if (this.content) {
      this.content.innerHTML = html;
      this.executeScripts();

      // Attach close buttons
      this.content.querySelectorAll(this.closeSelector).forEach((btn) => {
        btn.addEventListener('click', this.close);
      });

      if (typeof window.reinitFlowbiteWidgets === 'function') {
        window.reinitFlowbiteWidgets(this.content || undefined);
      }
    }
  }

  /**
   * Destroy the drawer instance
   */
  destroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
    this.drawer = null;
    this.backdrop = null;
    this.content = null;
  }
}

// Expose globally for backward compatibility
window.Drawer = Drawer;

export default Drawer;
